import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Play, Dumbbell, Zap, BarChart3, AlertTriangle, MapPin, ChevronRight, ArrowLeft } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const RESERVE_URL =
  "https://deporweb.deporweb.net/altaonline/nueva.aspx?header=true&footer=true&nav=false&lang=es-ES&type=WFBST0ZJVE5FU1N8NDY1#";
const LOGO =
  "https://customer-assets.emergentagent.com/job_261f6229-25c6-462a-bf7c-d1c15ef074eb/artifacts/rv1gzzra_logo%20v2%20xpro.png";

const DIFFICULTY = {
  1: { label: "Nivel 1 · Iniciación", color: "#4ade80", bg: "rgba(74,222,128,0.10)", border: "rgba(74,222,128,0.28)" },
  2: { label: "Nivel 2 · Intermedio", color: "#F2C230", bg: "rgba(242,194,48,0.10)", border: "rgba(242,194,48,0.30)" },
  3: { label: "Nivel 3 · Avanzado/Supervisado", color: "#fb923c", bg: "rgba(251,146,60,0.10)", border: "rgba(251,146,60,0.28)" },
};

export default function MachinePage() {
  const { numero } = useParams();
  const [machine, setMachine] = useState(null);
  const [exercises, setExercises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const meta = document.createElement("meta");
    meta.setAttribute("name", "robots");
    meta.setAttribute("content", "noindex, nofollow");
    document.head.appendChild(meta);
    const prevTitle = document.title;
    document.title = `Máquina ${numero} — XPRO FITNESS`;
    return () => {
      if (document.head.contains(meta)) document.head.removeChild(meta);
      document.title = prevTitle;
    };
  }, [numero]);

  useEffect(() => {
    Promise.all([
      fetch(`${API}/api/maquinas/${encodeURIComponent(numero)}`),
      fetch(`${API}/api/maquinas/${encodeURIComponent(numero)}/ejercicios`),
    ])
      .then(async ([mRes, exRes]) => {
        if (mRes.status === 404) { setNotFound(true); return; }
        if (!mRes.ok) throw new Error();
        const [m, exs] = await Promise.all([mRes.json(), exRes.ok ? exRes.json() : Promise.resolve([])]);
        setMachine(m);
        setExercises(Array.isArray(exs) ? exs : []);
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [numero]);

  if (loading) return <LoadingState />;
  if (notFound) return <NotFoundState numero={numero} />;
  return <MachineDetail machine={machine} exercises={exercises} />;
}

function LoadingState() {
  return (
    <div className="min-h-screen bg-black flex items-center justify-center" data-testid="machine-loading">
      <div className="w-8 h-8 border-2 border-[#F2C230] border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

function NotFoundState({ numero }) {
  return (
    <div
      className="min-h-screen bg-black text-white flex flex-col items-center justify-center px-6 text-center"
      data-testid="machine-not-found"
    >
      <img src={LOGO} alt="XPRO FITNESS" className="h-10 w-auto mb-8 opacity-60" />
      <div className="mb-6 w-16 h-16 rounded-full bg-white/5 border border-white/10 flex items-center justify-center">
        <AlertTriangle size={28} className="text-[#F2C230]" />
      </div>
      <h1 className="text-2xl font-black mb-3">Máquina no encontrada</h1>
      <p className="text-white/50 text-sm mb-2 max-w-xs leading-relaxed">
        No hemos encontrado ninguna máquina con el código{" "}
        <span className="font-black text-[#F2C230]">{numero}</span>.
      </p>
      <p className="text-white/35 text-sm">Avisa en recepción y lo solucionamos.</p>
      <a
        href="/"
        className="mt-10 text-xs text-white/30 hover:text-white/60 transition-colors tracking-widest uppercase"
      >
        ← Volver al inicio
      </a>
    </div>
  );
}

function MachineDetail({ machine, exercises }) {
  const diff = machine.dificultad ? DIFFICULTY[machine.dificultad] : null;
  const [imgError, setImgError] = useState(false);
  const [selectedEx, setSelectedEx] = useState(null);

  const hasMultipleExercises = exercises.length > 1;
  // For single-exercise machines use that exercise; for multi, use the selected one
  const activeExercise = hasMultipleExercises ? selectedEx : (exercises[0] || null);
  const videoUrl = activeExercise?.video_url || machine.video_url;
  const instrucciones = activeExercise?.instrucciones_breves || machine.instrucciones_breves;
  const hasVideo = !!videoUrl;

  const getYouTubeEmbed = (url) => {
    if (!url) return null;
    const match = url.match(
      /(?:youtube\.com\/(?:watch\?v=|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/
    );
    return match ? `https://www.youtube.com/embed/${match[1]}?rel=0&playsinline=1` : null;
  };
  const embedUrl = hasVideo ? getYouTubeEmbed(videoUrl) : null;

  return (
    <div className="min-h-screen bg-black text-white" data-testid="machine-detail">
      {/* Sticky header */}
      <header className="sticky top-0 z-10 bg-black/95 backdrop-blur border-b border-white/8">
        <div className="max-w-lg mx-auto px-4 h-14 flex items-center justify-between">
          <img src={LOGO} alt="XPRO FITNESS" className="h-7 w-auto" />
          <span
            className="bg-[#F2C230] text-black font-black text-sm tracking-widest px-3 py-1"
            data-testid="machine-numero-badge"
          >
            {machine.numero}
          </span>
        </div>
      </header>

      <div className="max-w-lg mx-auto px-4 pb-16">
        {/* Machine Image */}
        <div className="w-full aspect-video mt-6 mb-6 overflow-hidden bg-[#111] relative" data-testid="machine-image-container">
          {machine.foto_url && !imgError ? (
            <img
              src={machine.foto_url}
              alt={machine.nombre}
              className="w-full h-full object-cover"
              onError={() => setImgError(true)}
              data-testid="machine-photo"
            />
          ) : (
            <div className="absolute inset-0 flex flex-col items-center justify-center" data-testid="machine-photo-placeholder">
              <Dumbbell size={52} className="text-white/10 mb-3" />
              <span className="text-white/25 text-xs tracking-widest uppercase">Foto próximamente</span>
            </div>
          )}
        </div>

        {/* Name */}
        <h1 className="text-3xl sm:text-4xl font-black leading-tight mb-2" data-testid="machine-name">
          {machine.nombre}
        </h1>
        {machine.modelo && (
          <p className="text-white/35 text-xs tracking-[0.2em] uppercase mb-5">
            Ref. {machine.modelo}
          </p>
        )}

        {/* Tags */}
        <div className="flex flex-wrap gap-2 mb-7" data-testid="machine-tags">
          {machine.grupo_muscular && (
            <Tag icon={<Dumbbell size={11} />} label={machine.grupo_muscular} />
          )}
          {machine.tipo_esfuerzo && (
            <Tag icon={<Zap size={11} />} label={machine.tipo_esfuerzo} />
          )}
          {machine.planta && (
            <Tag icon={<BarChart3 size={11} />} label={`Planta ${machine.planta}`} />
          )}
          {diff && (
            <span
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-black tracking-widest uppercase"
              style={{ color: diff.color, background: diff.bg, border: `1px solid ${diff.border}` }}
              data-testid="machine-difficulty"
            >
              <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: diff.color }} />
              {diff.label}
            </span>
          )}
        </div>

        {/* Multi-exercise selector */}
        {hasMultipleExercises && !selectedEx && (
          <div className="mb-7" data-testid="exercise-list">
            <p className="text-[10px] tracking-[0.25em] uppercase text-[#F2C230] font-black mb-3">
              Ejercicios en esta máquina
            </p>
            <div className="space-y-2">
              {exercises.map((ex) => (
                <button
                  key={ex.id}
                  onClick={() => setSelectedEx(ex)}
                  data-testid={`exercise-item-${ex.codigo}`}
                  className="w-full flex items-center justify-between gap-4 px-4 py-3.5 border border-white/10 hover:border-[#F2C230]/50 bg-white/2 hover:bg-white/4 transition-all text-left group"
                >
                  <div>
                    <p className="font-black text-sm text-white group-hover:text-[#F2C230] transition-colors">{ex.nombre}</p>
                    {ex.dificultad && (
                      <p className="text-[10px] text-white/35 mt-0.5">{DIFFICULTY[ex.dificultad]?.label}</p>
                    )}
                  </div>
                  <ChevronRight size={16} className="text-white/25 group-hover:text-[#F2C230] shrink-0 transition-colors" />
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Back to list button when an exercise is selected */}
        {hasMultipleExercises && selectedEx && (
          <button
            onClick={() => setSelectedEx(null)}
            data-testid="back-to-exercise-list"
            className="flex items-center gap-2 text-white/40 hover:text-white text-xs tracking-widest uppercase font-bold mb-5 transition-colors"
          >
            <ArrowLeft size={13} /> Todos los ejercicios
          </button>
        )}

        {/* Selected exercise name (multi-exercise mode) */}
        {hasMultipleExercises && selectedEx && (
          <p className="text-[10px] tracking-[0.25em] uppercase text-[#F2C230] font-black mb-3">
            {selectedEx.nombre}
          </p>
        )}

        {/* Video section — shown for single exercise or after selection */}
        {(!hasMultipleExercises || selectedEx) && (
          <div className="mb-7">
            {embedUrl ? (
              <div className="mx-auto w-full max-w-[300px]" data-testid="video-embed">
                {/* 9:16 portrait container — Short / Reel style */}
                <div className="relative aspect-[9/16] overflow-hidden rounded-2xl bg-black ring-1 ring-white/10 shadow-[0_0_40px_rgba(0,0,0,0.8)]">
                  <iframe
                    src={embedUrl}
                    title="Tutorial"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="absolute inset-0 w-full h-full"
                  />
                </div>
                <p className="text-center text-[10px] text-white/25 mt-2 tracking-widest uppercase">Tutorial</p>
              </div>
            ) : hasVideo ? (
              <a
                href={videoUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full flex items-center justify-center gap-3 bg-[#F2C230] text-black font-black text-sm tracking-widest uppercase py-4 hover:bg-white transition-colors"
                data-testid="video-btn"
              >
                <Play size={18} fill="black" />
                Ver vídeo tutorial
              </a>
            ) : (
              <button
                disabled
                className="w-full flex items-center justify-center gap-3 border border-white/10 text-white/25 font-black text-sm tracking-widest uppercase py-4 cursor-not-allowed"
                data-testid="video-btn-disabled"
              >
                <Play size={18} />
                Vídeo próximamente
              </button>
            )}
          </div>
        )}

        {/* Instructions */}
        {instrucciones && (!hasMultipleExercises || selectedEx) && (
          <div className="mb-8 p-5 border border-white/8 bg-white/3" data-testid="machine-instructions">
            <p className="text-[10px] tracking-[0.25em] uppercase text-[#F2C230] font-black mb-2">
              Cómo usar esta máquina
            </p>
            <p className="text-sm text-white/75 leading-relaxed">{instrucciones}</p>
          </div>
        )}

        {/* CTA */}
        <div className="border-t border-white/8 pt-8 text-center">
          <div className="flex items-center justify-center gap-2 mb-3">
            <MapPin size={13} className="text-white/30" />
            <span className="text-white/30 text-xs">Carrer Sils, 17 · Vidreres, Girona</span>
          </div>
          <p className="text-white/35 text-xs mb-5">¿Quieres entrenar en XPRO FITNESS?</p>
          <a
            href={RESERVE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block bg-[#F2C230] text-black font-black text-xs tracking-[0.25em] uppercase px-8 py-4 hover:bg-white transition-colors"
            data-testid="machine-cta"
          >
            Hazte Socio
          </a>
        </div>
      </div>
    </div>
  );
}

function Tag({ icon, label }) {
  return (
    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/5 border border-white/10 text-[11px] text-white/65 font-bold tracking-widest uppercase">
      {icon}
      {label}
    </span>
  );
}
